import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TItem, TStats } from '@modules/items';
import { addItemsToFolderQueryData } from '../items/api/itemQueryData';
import { TERMINOLOGY } from '@shared/i18n';
import {
  TUseInlineBlockResult,
  TInlineBlockDraft,
  TInlineBlockVersion,
  TSaveInlineBlockResult,
  TCompleteInlineBlockResult,
  TSetTagResult,
} from './types';

// --- GET:

export const useInlineBlock = (id: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const fetchFn = async (): Promise<TUseInlineBlockResult> => {
    const res = await apiClient.get(`/inline-blocks/${id}`);
    res.data.inlineBlock.versions.reverse();
    return res.data;
  };

  const { data, isError, error } = useQuery({
    queryKey: [`inline-block-${id}`],
    queryFn: fetchFn,
    retry,
  });

  useHandleQueryError(isError, error, `Failed to load inline-block ${id}!`, 'Loading inline-block failed!');

  return { data, error };
};

// --- CREATE:

export const useCreateInlineBlock = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { parent: string; name: string; initValue?: string }): Promise<TItem> => {
    const res = await apiClient.post('/inline-blocks', variables);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Creating ${TERMINOLOGY.inlineBlock.lower} failed!`, true),
    onSuccess: (created, variables) => {
      show('success', `${TERMINOLOGY.inlineBlock.upper} created.`);
      addItemsToFolderQueryData(queryClient, variables.parent, [created]);
    },
  });
};

export const useCreateQualioInlineBlock = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { parent: string; url: string }): Promise<TItem> => {
    const res = await apiClient.post('/integrations/qualio/inline-blocks', variables);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Creating Qualio ${TERMINOLOGY.inlineBlock.lower} failed!`, true),
    onSuccess: (created, variables) => {
      show('success', `Qualio ${TERMINOLOGY.inlineBlock.lower} created.`);
      addItemsToFolderQueryData(queryClient, variables.parent, [created]);
    },
  });
};

// --- EDIT DRAFT:

export const useEditInlineBlockDraft = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (id: string): Promise<{ draft: TInlineBlockDraft; stats: TStats }> => {
    const res = await apiClient.put(`/inline-blocks/${id}/draft`);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to load draft!', true),
    onSuccess: ({ draft, stats }, id) => {
      queryClient.setQueryData([`inline-block-${id}`], (old: TUseInlineBlockResult | undefined) =>
        old
          ? {
              ...old,
              inlineBlock: { ...old.inlineBlock, draft, stats },
            }
          : old
      );
    },
  });
};

// --- SAVE (update value):

export const useSaveInlineBlock = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (vars: {
    id: string;
    value: string;
    url?: string | null;
  }): Promise<TSaveInlineBlockResult> => {
    const res = await apiClient.put(`/inline-blocks/${vars.id}/value`, {
      value: vars.value,
      url: vars.url,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving ${TERMINOLOGY.inlineBlock.lower} failed!`, true),
    onSuccess: ({ draft, stats }, vars) => {
      queryClient.setQueryData([`inline-block-${vars.id}`], (old: TUseInlineBlockResult | undefined) =>
        old
          ? {
              ...old,
              inlineBlock: { ...old.inlineBlock, draft, stats },
            }
          : old
      );
    },
  });
};

// --- COMPLETE:

export const useCompleteInlineBlock = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (vars: {
    id: string;
    version: string;
    note: string;
  }): Promise<TCompleteInlineBlockResult> => {
    const res = await apiClient.put(`/inline-blocks/${vars.id}/complete`, {
      version: vars.version,
      note: vars.note,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Completing ${TERMINOLOGY.inlineBlock.lower} failed!`, true),
    onSuccess: ({ version, stats }, { id }) => {
      queryClient.setQueryData([`inline-block-${id}`], (old: TUseInlineBlockResult | undefined) =>
        old
          ? {
              ...old,
              inlineBlock: {
                ...old.inlineBlock,
                versions: [version, ...old.inlineBlock.versions],
                stats,
                draft: undefined, // reset draft after completion
              },
            }
          : old
      );
      show('success', 'Draft completed.');
    },
  });
};

// --- TAG USAGE:

type TTagUsageResult = { taggedSnippetCount: number };

export const useGetTagUsage = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (id: string): Promise<TTagUsageResult> => {
    const res = await apiClient.get(`/inline-blocks/${id}/tag-usage`);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to check tag usage.', true),
  });
};

// --- SET TAG:

export const useSetInlineBlockTag = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (vars: { id: string; blockTagId: string | null }): Promise<TSetTagResult> => {
    const res = await apiClient.put(`/inline-blocks/${vars.id}/tag`, {
      blockTagId: vars.blockTagId,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Updating tag failed!', true),
    onSuccess: ({ blockTagId, stats }, vars) => {
      show('success', 'Tag updated.');
      queryClient.setQueryData([`inline-block-${vars.id}`], (old: TUseInlineBlockResult | undefined) =>
        old
          ? {
              ...old,
              inlineBlock: { ...old.inlineBlock, blockTagId: blockTagId ?? undefined, stats },
            }
          : old
      );
    },
  });
};

// --- SET TAGGED BLOCK FOR FOLDER:

export const useSetTaggedBlockForFolder = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (vars: {
    folderId: string;
    blockTagId: string;
    blockId: string;
  }): Promise<{ updatedCount: number }> => {
    const res = await apiClient.put(`/folders/${vars.folderId}/tagged-block`, {
      blockTagId: vars.blockTagId,
      blockId: vars.blockId,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Setting tagged block for folder failed!', true),
    onSuccess: ({ updatedCount }) => {
      const msg =
        updatedCount > 0
          ? `Set for ${updatedCount} document${updatedCount === 1 ? '' : 's'}.`
          : 'No documents found in this product.';
      show('success', msg);
    },
  });
};
